import {
  GetOrders200Response,
  InstrumentTypeResponse,
  OrderStatusResponse,
  OrderTypeResponse,
  SideResponse,
  SystemTypeResponse,
} from "../../../codegen-api";
import { getCurrentChainConfig } from "../../../config/chains";
import { getMarketDecimals, rawToDecimal } from "../../../utils/rawConversion";

type RealtimeOpenOrderQuery = Record<
  string,
  string | number | boolean | undefined
>;

export interface RealtimeOpenOrdersPage<T> {
  data: T[];
  nextCursor?: string;
  hasNext?: boolean;
  count?: number;
}

const normalize = (value: unknown) =>
  String(value ?? "").trim().toUpperCase();

const getInstrumentName = (order: Record<string, unknown>) =>
  String(
    order.instrument_name ||
      order.instrumentName ||
      order.market ||
      order.symbol ||
      ""
  );

const getOrderAsset = (order: Record<string, unknown>) => {
  const explicit =
    order.asset || order.underlying_asset || order.underlyingAsset;
  if (explicit) return normalize(explicit);

  const instrumentName = getInstrumentName(order);
  const [asset] = instrumentName.split("-");
  return normalize(asset);
};

const getOrderInstrumentType = (order: Record<string, unknown>) =>
  normalize(order.instrument_type || order.instrumentType);

const orderTimestamp = (order: Record<string, unknown>) => {
  const raw = order.created_timestamp || order.timestamp || 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const getOrderId = (order: Record<string, unknown>) =>
  String(order.order_id || order.orderId || order.id || "");

const isTerminalOrderEvent = (action?: unknown, status?: unknown) => {
  const normalizedAction = normalize(action).toLowerCase();
  const normalizedStatus = normalize(status);
  return (
    normalizedAction === "cancel" ||
    normalizedAction === "cancelled" ||
    normalizedAction === "filled" ||
    normalizedAction === "fill" ||
    normalizedStatus === "CANCELLED" ||
    normalizedStatus === "FILLED" ||
    normalizedStatus === "EXPIRED" ||
    normalizedStatus === "REJECTED"
  );
};

const mapStatus = (status?: unknown): OrderStatusResponse => {
  switch (normalize(status)) {
    case "FILLED":
      return OrderStatusResponse.Filled;
    case "PARTIALLY_FILLED":
      return OrderStatusResponse.Partial;
    case "CANCELLED":
      return OrderStatusResponse.Cancelled;
    case "EXPIRED":
      return OrderStatusResponse.Expired;
    case "REJECTED":
    case "FAILED":
      return OrderStatusResponse.Rejected;
    default:
      return OrderStatusResponse.Opened;
  }
};

const configuredPerpMarketName = (marketAddress?: unknown) => {
  const normalizedMarket = String(marketAddress || "").toLowerCase();
  if (!normalizedMarket) return undefined;
  const markets = getCurrentChainConfig().contracts.perpetualDex?.markets || {};
  return Object.entries(markets).find(
    ([, address]) => String(address).toLowerCase() === normalizedMarket,
  )?.[0];
};

export const buildRealtimeOpenOrder = (
  rawOrder?: Record<string, unknown>,
): GetOrders200Response | undefined => {
  if (!rawOrder) return undefined;

  const orderId = getOrderId(rawOrder);
  const market = rawOrder.poolId || rawOrder.instrument_id;
  if (!orderId || !market) return undefined;

  const currentChainId = String(getCurrentChainConfig().chainId);
  const eventChainId = rawOrder.chainId ?? rawOrder.chain_id;
  if (
    eventChainId !== undefined &&
    eventChainId !== null &&
    String(eventChainId) !== currentChainId
  ) {
    return undefined;
  }

  const instrumentName =
    String(rawOrder.instrument_name || rawOrder.instrumentName || "") ||
    configuredPerpMarketName(market) ||
    String(market);
  const timestamp = String(rawOrder.timestamp || Date.now());

  // Perps are 18/18 everywhere. Everything else must be looked up: this used
  // to scale every order by a hardcoded 18, which is simply wrong on a 6-dp
  // quote market, and the number lands on an open order a trader may act on.
  const isPerp = instrumentName.endsWith("-PERP");
  const decimals = isPerp
    ? { baseDecimals: 18, quoteDecimals: 18 }
    : getMarketDecimals(instrumentName) ?? getMarketDecimals(String(market));
  if (!decimals) return undefined;

  return {
    order_id: orderId,
    account: String(rawOrder.user || rawOrder.account || ""),
    instrument_id: String(market),
    instrument_name: instrumentName,
    instrument_type: instrumentName.endsWith("-PERP")
      ? InstrumentTypeResponse.Perpetual
      : InstrumentTypeResponse.Spot,
    order_type:
      normalize(rawOrder.type || rawOrder.order_type) === "MARKET"
        ? OrderTypeResponse.Market
        : OrderTypeResponse.Limit,
    side:
      normalize(rawOrder.side).toLowerCase() === "sell"
        ? SideResponse.Sell
        : SideResponse.Buy,
    amount: rawToDecimal(
      String(rawOrder.quantity || rawOrder.amount || "0"),
      decimals.baseDecimals,
    ),
    price: rawToDecimal(String(rawOrder.price || "0"), decimals.quoteDecimals),
    avg_price: rawToDecimal(
      String(rawOrder.avgExecutionPrice || rawOrder.avg_price || rawOrder.price || "0"),
      decimals.quoteDecimals,
    ),
    filled: rawToDecimal(String(rawOrder.filled || "0"), decimals.baseDecimals),
    order_status: mapStatus(rawOrder.status || rawOrder.order_status),
    post_only: false,
    reduce_only: Boolean(rawOrder.reduceOnly ?? rawOrder.reduce_only ?? false),
    timestamp,
    created_timestamp: timestamp,
    system_type: SystemTypeResponse.Web,
  };
};

const mergeOpenOrder = <T>(
  orders: T[],
  nextOrder: T,
): T[] => {
  const nextId = getOrderId(nextOrder as Record<string, unknown>);
  return [nextOrder, ...orders.filter((order) => {
    const orderId = getOrderId(order as Record<string, unknown>);
    return !nextId || orderId !== nextId;
  })].sort(
    (a, b) =>
      orderTimestamp(b as Record<string, unknown>) -
      orderTimestamp(a as Record<string, unknown>),
  );
};

const removeOpenOrder = <T>(orders: T[], orderId: string): T[] =>
  orders.filter(
    (order) => getOrderId(order as Record<string, unknown>) !== orderId,
  );

export const filterRealtimeOpenOrders = <T>(
  orders: T[],
  query?: RealtimeOpenOrderQuery,
): T[] => {
  const wantedType = normalize(query?.instrument_type);
  const wantedAsset = normalize(query?.asset);

  return orders
    .filter((order) => {
      const record = order as Record<string, unknown>;

      if (
        wantedType &&
        getOrderInstrumentType(record) &&
        getOrderInstrumentType(record) !== wantedType
      ) {
        return false;
      }

      if (wantedAsset && getOrderAsset(record) !== wantedAsset) {
        return false;
      }

      return true;
    })
    .sort(
      (a, b) =>
        orderTimestamp(b as Record<string, unknown>) -
        orderTimestamp(a as Record<string, unknown>),
    );
};

export const applyRealtimeOpenOrdersSnapshot = <T>(
  pages: RealtimeOpenOrdersPage<T>[] | undefined,
  snapshotOrders: T[],
  query?: RealtimeOpenOrderQuery,
): RealtimeOpenOrdersPage<T>[] => {
  const filtered = filterRealtimeOpenOrders(snapshotOrders, query);
  const firstPage = pages?.[0] || { data: [] };

  return [
    {
      ...firstPage,
      data: filtered,
      nextCursor: undefined,
      hasNext: false,
      count: filtered.length,
    },
  ];
};

export const applyRealtimeOpenOrderEvent = <T>(
  pages: RealtimeOpenOrdersPage<T>[] | undefined,
  rawOrder: Record<string, unknown> | undefined,
  action?: unknown,
  query?: RealtimeOpenOrderQuery,
): RealtimeOpenOrdersPage<T>[] | undefined => {
  if (!rawOrder) return pages;

  const orderId = getOrderId(rawOrder);
  if (!orderId) return pages;

  const existingPages = pages?.length ? pages : [{ data: [] as T[] }];
  if (isTerminalOrderEvent(action, rawOrder.status || rawOrder.order_status)) {
    return existingPages.map((page) => {
      const data = removeOpenOrder(page.data, orderId);
      return {
        ...page,
        data,
        count:
          page.count !== undefined
            ? Math.min(page.count, data.length)
            : page.count,
      };
    });
  }

  const nextOrder = buildRealtimeOpenOrder(rawOrder);
  if (!nextOrder) return pages;
  if (filterRealtimeOpenOrders([nextOrder], query).length === 0) return pages;

  const [firstPage, ...rest] = existingPages;
  const firstData = mergeOpenOrder(firstPage.data, nextOrder as T);
  return [
    {
      ...firstPage,
      data: firstData,
      count:
        firstPage.count !== undefined
          ? Math.max(firstPage.count, firstData.length)
          : firstData.length,
    },
    ...rest.map((page) => ({
      ...page,
      data: removeOpenOrder(page.data, orderId),
    })),
  ];
};
